import { forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import shareLinkBackground from "./sharelinkbackground.jpeg";

export interface MemorialGraphic1080Props {
  fullName: string;
  birthDate: string;
  deathDate: string;
  funeralDateText: string;
  venueText: string;
  imageUrl: string;
  patternSvgUrl: string;
  livestreamedBy?: string;
  topLine?: string;
  secondLine?: string;
  funeralStartTime?: string;
  imageSize?: number;
  patternSize?: number;
  previewSize?: number;
  providedBy?: { name: string; logo: string };
}

const BASE_WIDTH = 1080;
const BASE_HEIGHT = 1080;

const formatFuneralDate = (value: string): string => {
  const parts = value.split("-");
  if (parts.length !== 3) return value;

  const [day, month, year] = parts;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatFuneralTime = (value?: string): string =>
  value ? value.slice(0, 5) : "";

const MemorialGraphic1080 = forwardRef<
  HTMLDivElement,
  MemorialGraphic1080Props
>(
  (
    {
      fullName,
      // birthDate,
      // deathDate,
      funeralDateText,
      venueText,
      imageUrl,
      patternSvgUrl,
      funeralStartTime,
      livestreamedBy = "MUZUKURU",
      topLine = "In loving",
      secondLine = "MEMORY OF",
      imageSize = 420,
      patternSize = 600,
      previewSize = 300,
      providedBy,
    },
    exportRef,
  ) => {
    const scale = previewSize / BASE_WIDTH;

    return (
      <Box
        sx={{
          width: previewSize,
          height: previewSize * (BASE_HEIGHT / BASE_WIDTH),
          position: "relative",
          overflow: "hidden",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            width: BASE_WIDTH,
            height: BASE_HEIGHT,
            position: "absolute",
            top: 0,
            left: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
        <Box
          ref={exportRef}
          sx={{
            width: BASE_WIDTH,
            height: BASE_HEIGHT,
            overflow: "hidden",
            backgroundImage: `url(${shareLinkBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            pt: 6,
          }}
        >
          {/* soft paper texture */}
          {/* <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.07,
              pointerEvents: "none",
              backgroundImage: `
                radial-gradient(rgba(80,80,80,0.22) 0.7px, transparent 0.7px),
                radial-gradient(rgba(120,120,120,0.15) 0.6px, transparent 0.6px)
              `,
              backgroundSize: "12px 12px, 18px 18px",
              backgroundPosition: "0 0, 6px 6px",
            }}
          /> */}

          {/* heading */}
          <Box
            sx={{
              textAlign: "center",
              zIndex: 3,
            }}
          >
            <Typography
              sx={{
                fontFamily: `'Le Jour Script', 'Brush Script MT', cursive`,
                fontSize: "128px",
                lineHeight: 0.84,
                color: "#5a5b51",
                fontWeight: 400,
                letterSpacing: "0.01em",
              }}
            >
              {topLine}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Montserrat, sans-serif",
                mt: 0.5,
                fontSize: "27px",
                letterSpacing: "0.28em",
                fontWeight: 800,
                color: "#5d5d55",
                textTransform: "uppercase",
              }}
            >
              {secondLine}
            </Typography>
          </Box>

          {/* wreath + portrait */}
          <Box
            sx={{
              position: "relative",
              mt: -3,
              width: patternSize,
              height: patternSize,
              zIndex: 2,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={patternSvgUrl}
              alt="Memorial wreath"
              crossOrigin="anonymous"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: patternSize,
                height: patternSize,
                objectFit: "contain",
                zIndex: 1,
                pointerEvents: "none",
                userSelect: "none",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: imageSize,
                height: imageSize,
                borderRadius: "50%",
                overflow: "hidden",
                zIndex: 2,
                backgroundColor: "#d9d9d6",
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt={fullName}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  filter: "none",
                }}
              />
            </Box>
          </Box>

          {/* name and dates */}
          <Box
            sx={{
              mt: -2.5,
              textAlign: "center",
              zIndex: 3,
              px: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* name */}
            <Typography
              sx={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "54px",
                fontWeight: 400,
                letterSpacing: "0.12em",
                color: "#56584f",
                textTransform: "uppercase",
                lineHeight: 1.1,
              }}
            >
              {fullName}
            </Typography>

            {/* funeral date + time */}
            <Typography
              sx={{
                fontFamily: "Montserrat, sans-serif",
                mt: 1,
                fontSize: "32px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "#3d3d37",
                textTransform: "uppercase",
                lineHeight: 1.3,
              }}
            >
              {formatFuneralDate(funeralDateText)}, AT {formatFuneralTime(funeralStartTime)}HRS (CAT)
            </Typography>

            <Typography
              sx={{
                fontFamily: "Montserrat, sans-serif",
                mt: 1,
                fontSize: "20px",
                letterSpacing: "0.1em",
                color: "#5e5e57",
                textTransform: "uppercase",
                lineHeight: 1.4,
                fontWeight: 400,
                maxWidth: "80%",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {venueText}
            </Typography>

            {providedBy ? (
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "18px",
                      letterSpacing: "0.14em",
                      color: "#3d3d37",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    Service livestreamed by:
                  </Typography>

                  <Box
                    component="img"
                    src={livestreamedBy}
                    alt="Livestreamed by"
                    sx={{
                      mt: 0.5,
                      height: "88px",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    width: "2px",
                    alignSelf: "stretch",
                    bgcolor: "#232320",
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "18px",
                      letterSpacing: "0.14em",
                      color: "#3d3d37",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    Service provided by:
                  </Typography>

                  <Box
                    sx={{
                      mt: 0.5,
                      background:
                        "linear-gradient(135deg, #009677 0%, #50b848 55%, #8dc63f 100%)",
                      borderRadius: "10px",
                      px: 2.5,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={providedBy.logo}
                      alt={providedBy.name}
                      sx={{
                        height: "62px",
                        width: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "18px",
                    letterSpacing: "0.14em",
                    color: "#3d3d37",
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Service livestreamed by:
                </Typography>

                <Box
                  component="img"
                  src={livestreamedBy}
                  alt="Livestreamed by"
                  sx={{
                    mt: 0.5,
                    height: "100px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
        </Box>
      </Box>
    );
  },
);

MemorialGraphic1080.displayName = "MemorialGraphic1080";

export default MemorialGraphic1080;